import { useTranslation } from 'react-i18next';
import Button from '../../Button';
import React, { useEffect, useRef } from 'react';
import LoadingButton from '../../LoadingButton';
import { CloseIcon } from '../../Icons';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { createPortal } from 'react-dom';

export type ModalProps = {
	isOpen: boolean;
	onClose: () => void;
	onExitComplete?: () => void;
	title?: string;
	children: React.ReactNode;

	confirmText?: string;
	confirmIcon?: React.ReactNode;
	confirmIconPosition?: 'left' | 'right';
	confirmDisabled?: boolean;
	buttonConfirmType?: 'submit' | 'button';
	cancelText?: string;
	leftAction?: React.ReactNode;

	formId?: string;
	isLoading?: boolean;

	subtitle?: string | React.ReactNode;
	onConfirm?: () => void;
	onCancel?: () => void;
	scrollContainerRef?: React.RefObject<HTMLDivElement>;
	widthStyles?: string;
	hideFooter?: boolean;
	hideConfirm?: boolean;

	premiumBackground?: boolean;
	showScrollbar?: boolean;
};

const Modal: React.FC<ModalProps> = ({
	isOpen,
	onClose,
	onExitComplete,
	title,
	children,
	confirmText = 'confirm',
	confirmIcon,
	confirmIconPosition = 'left',
	confirmDisabled = false,
	leftAction,
	cancelText = 'cancel',
	formId,
	isLoading,
	buttonConfirmType,
	onConfirm,
	onCancel,
	subtitle,
	scrollContainerRef,
	widthStyles,
	hideFooter = false,
	hideConfirm = false,
	premiumBackground = false,
	showScrollbar = true,
}) => {
	const { t } = useTranslation();
	const internalScrollRef = useRef<HTMLDivElement>(null);
	const resolvedScrollRef = scrollContainerRef ?? internalScrollRef;

	useEffect(() => {
		if (isOpen) {
			document.body.classList.add('overflow-hidden');
		} else {
			document.body.classList.remove('overflow-hidden');
		}
		return () => {
			document.body.classList.remove('overflow-hidden');
		};
	}, [isOpen]);

	const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	const modalClasses = clsx(
		'rounded-xl w-full max-w-[calc(100vw-10%)] md:max-w-[38.313rem] relative max-h-[calc(100vh-10%)] flex flex-col p-0',
		widthStyles,
		premiumBackground ? 'bg-premium-gradient' : 'bg-light',
	);

	return createPortal(
		<AnimatePresence onExitComplete={onExitComplete}>
			{isOpen && (
				<div
					className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50'
					onClick={handleOutsideClick}
				>
					<motion.div
						initial={{ opacity: 0, scale: 0 }}
						animate={{ opacity: 1, scale: 1 }}
						exit={{ opacity: 0, scale: 0.95 }}
						className={modalClasses}
					>
						<header className='px-8 pt-8 pb-4'>
							<div className='flex justify-between items-center flex-shrink-0'>
								<h2 className='text-primary font-semibold text-3xl first-letter-uppercase'>
									{t(title as string)}
								</h2>
								<Button
									size='icon'
									style='ghost'
									onClick={onClose}
									iconLeft={
										<CloseIcon
											width={24}
											height={24}
										/>
									}
								/>
							</div>
							<p className='first-letter-uppercase'>
								{typeof subtitle === 'string' ? t(subtitle) : subtitle}
							</p>
						</header>
						<main
							ref={resolvedScrollRef}
							className={clsx(
								'mx-6 px-2 flex-grow overflow-y-auto overflow-x-hidden max-h-[60vh]  relative' +
									(hideFooter ? ' pb-8' : ''),
								!showScrollbar ? 'scrollbar-hidden' : 'custom-scrollbar',
							)}
						>
							{children}
						</main>
						{!hideFooter && (
							<footer className='flex flex-wrap justify-between px-8 pb-8 pt-6 flex-shrink-0'>
								{leftAction && !isLoading && leftAction}

								<div className={`${leftAction ? '' : 'w-full'} flex justify-end gap-4`}>
									{isLoading ? (
										<LoadingButton />
									) : (
										<>
											{onCancel && (
												<Button
													text={t(cancelText)}
													onClick={onCancel}
													style='link'
												/>
											)}
											{!hideConfirm && (
												<Button
													text={t(confirmText)}
													onClick={onConfirm}
													type={buttonConfirmType}
													disabled={confirmDisabled}
													iconLeft={
														confirmIcon && confirmIconPosition === 'left' ? confirmIcon : undefined
													}
													iconRight={
														confirmIcon && confirmIconPosition === 'right' ? confirmIcon : undefined
													}
													form={formId}
													style='highlighted'
												/>
											)}
										</>
									)}
								</div>
							</footer>
						)}
					</motion.div>
				</div>
			)}
		</AnimatePresence>,
		document.body,
	);
};

export default Modal;
